/**
 * Ask the server whether Same-Day is open for a date.
 *
 * A request, not a calculation, and that is the whole point — see
 * `docs/adr/0004-same-day-availability-server-side-only.md`. This app holds no
 * cutoffs and its clock is the customer's, so a browser in Los Angeles working it
 * out locally would offer a New York window three hours after it closed.
 *
 * Not cached, for the same reason: the answer narrows through the day, and a
 * cached "open" would keep offering a window past its cutoff.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  SameDayAnswer,
  fetchSameDayAvailability,
} from "../lib/pricing-config";

export interface SameDayState {
  answer: SameDayAnswer | null;
  /**
   * Whether the *first* answer for the current date is still in flight. A poll
   * refreshing an answer already on screen is not loading — reporting it as
   * loading would blank the window list every time the poll came round.
   */
  loading: boolean;
  /**
   * Whether Same-Day can actually be booked for the requested date right now.
   *
   * Windows are part of the question, not a detail of it. The endpoint can
   * answer `available: true` with an empty list, and a caller that read only the
   * flag would offer a way in to a screen with nothing on it.
   */
  available: boolean;
  /**
   * Whether we are currently unable to reach the availability endpoint.
   *
   * Distinct from `available: false`, which is the server telling us there are
   * no windows. Collapsing the two is how a single network blip told a customer
   * "today is fully booked" and deleted the window they had already chosen.
   */
  unreachable: boolean;
  /** Re-ask. */
  refresh: () => void;
}

export function useSameDayAvailability(
  serviceDate: string | undefined,
  cleaningType?: string,
  /**
   * Re-ask this often, in milliseconds. Off by default.
   *
   * Worth switching on wherever the windows are on screen: the set narrows
   * through the day, and a customer who opened the step at 2:55 and is still
   * reading it at 3:05 is looking at an answer that has since expired. Without
   * this the only thing that re-asks is a reload, so a window stays bookable
   * long after it closed — the customer picks it, and the server refuses the
   * booking they were shown.
   */
  pollMs = 0
): SameDayState {
  const [answer, setAnswer] = useState<SameDayAnswer | null>(null);
  const [settled, setSettled] = useState(false);
  const [nonce, setNonce] = useState(0);
  /**
   * Consecutive failed requests for the current question.
   *
   * A single failure keeps the last good answer on screen — a poll that could
   * not reach the server has learned nothing, and throwing away a correct answer
   * because of it is strictly worse than keeping it for another interval. Only a
   * run of them is treated as "we cannot check", and even then the last answer
   * stays visible with the trouble said out loud rather than being replaced by a
   * "fully booked" screen that is not what the server said.
   */
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const FAILURES_BEFORE_UNREACHABLE = 3;

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  /**
   * One string for the question being asked, so a change of date or of cleaning
   * type resets the answer rather than leaving the previous date's windows on
   * screen while the new date's request is in flight.
   */
  const question = `${serviceDate ?? ""}|${cleaningType ?? ""}`;
  const asked = useRef(question);
  if (asked.current !== question) {
    asked.current = question;
    // Set during render rather than in an effect: an effect would paint one
    // frame of the old date's windows under the new date's heading first.
    setAnswer(null);
    setSettled(false);
    setConsecutiveFailures(0);
  }

  useEffect(() => {
    if (!serviceDate) {
      setAnswer(null);
      setSettled(true);
      return;
    }

    let cancelled = false;

    fetchSameDayAvailability(serviceDate, cleaningType)
      .then((result) => {
        if (cancelled) return;
        setAnswer(result);
        setConsecutiveFailures(0);
      })
      .catch(() => {
        /**
         * A failed request is not an answer.
         *
         * This used to `setAnswer(null)`, on the reasoning that treating failure
         * as "not available" is the safe direction. It is the safe direction for
         * a *first* request, where there is nothing to fall back on — but this
         * hook polls every minute, and applying it to a poll meant one blip
         * turned a screen full of windows into "Today is fully booked" and wiped
         * the selection the customer had already made. The next poll brought the
         * windows back with nothing chosen and no explanation.
         *
         * So: keep whatever the server last actually said, count the failure,
         * and let the caller decide how to speak about a run of them. On the
         * first request there is still no answer to keep, so the closed state is
         * reached anyway — which is the case the original reasoning was about.
         */
        if (!cancelled) setConsecutiveFailures((n) => n + 1);
      })
      .finally(() => {
        if (!cancelled) setSettled(true);
      });

    // Guards against a slow answer for an old date landing after a newer one and
    // overwriting it — the customer changes dates faster than the network replies.
    return () => {
      cancelled = true;
    };
  }, [serviceDate, cleaningType, nonce]);

  useEffect(() => {
    if (!pollMs || !serviceDate) return;

    const timer = setInterval(refresh, pollMs);
    /**
     * Also on the way back to the tab. A backgrounded tab throttles its timers
     * to once a minute or worse, so someone returning after an hour would
     * otherwise act on an hour-old answer before the next tick arrived.
     */
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [pollMs, serviceDate, refresh]);

  return {
    answer,
    loading: !settled,
    available: Boolean(answer?.available) && (answer?.windows?.length ?? 0) > 0,
    unreachable: consecutiveFailures >= FAILURES_BEFORE_UNREACHABLE,
    refresh,
  };
}
